import styled from "@emotion/styled";
import { forwardRef, useMemo, type DetailedHTMLProps, type HTMLAttributes } from "react";

import { useFormatMessage, type FormatMessages } from "@skilitics/localization";

import { AiBadge, SkiliticsIcon, type SkiliticsIconName } from "../../../icons";
import { useMediaAssetSrc } from "../../media/useMediaAssetSrc";
import type { FileMediaAssetValue, MediaAssetType } from "../types";

const Container = styled.div<{ $clickable?: boolean }>`
  position: relative;

  display: flex;
  align-items: center;
  max-width: 100%;
  min-width: 0;

  user-select: none;
  cursor: ${({ $clickable }) => ($clickable ? "pointer" : "default")};
`;

const Label = styled.div`
  height: 30px;

  line-height: 28px;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const LabelContainer = styled.div`
  flex: 1 1 auto;
  min-width: 0;

  border-bottom: 1px solid ${({ theme }) => theme.outlineSoft};

  overflow: hidden;
  word-break: break-all;
`;

const ThumbnailContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  width: 26px;
  height: 26px;
  flex: 0 0 26px;

  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;

  border-radius: ${({ theme }) => theme.borderRadius}px;
  border: solid 1px ${({ theme }) => theme.outlineSoft};
  margin-right: 10px;
`;

const StyledAiBadge = styled(AiBadge)`
  position: absolute;

  top: -3px;
  left: 17px;
`;

const iconByMediaAssetType: Record<MediaAssetType, SkiliticsIconName> = {
  audio: "layer-audio",
  image: "layer-image",
  video: "layer-video",
  model: "layer-3d",
};

type UploadedMediaRowProps = DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement> & {
  media: FileMediaAssetValue;
  clickable?: boolean;
  showAiBadge?: boolean;
  type: MediaAssetType;
};

export const UploadedMediaRow = forwardRef<HTMLDivElement, UploadedMediaRowProps>(
  // eslint-disable-next-line @typescript-eslint/no-shadow -- function named after the const for devtools
  function UploadedMediaRow({ media, clickable, showAiBadge = false, type = "image", ...props }, ref) {
    const formatMessage = useFormatMessage(formatStrings);

    const icon = iconByMediaAssetType[type];

    // note: only show media thumbnail for images (do not begin download of video assets etc.)
    const url = useMediaAssetSrc(type === "image" ? media : undefined);

    const style = useMemo(
      () => (url && type === "image" ? { backgroundImage: `url(${url})` } : undefined),
      [url, type],
    );

    return (
      <Container ref={ref} $clickable={clickable} {...props}>
        <ThumbnailContainer style={style}>
          {!style && icon && <SkiliticsIcon icon={icon} size={14} />}
        </ThumbnailContainer>
        <LabelContainer>
          <Label>
            {media.name ||
              (!url
                ? formatMessage("loading")
                : // Filename should always be captured, however older media may not have a filename.
                  "Uploaded Media")}
          </Label>
        </LabelContainer>
        {showAiBadge && <StyledAiBadge />}
      </Container>
    );
  },
);

const formatStrings = {
  loading: {
    en: "Loading...",
  },
} satisfies FormatMessages;
